import React from 'react';

interface AIModelCostItem {
  model_name: string;
  provider: string;
  cost?: number | string;
  input_cost?: number | string;
  output_cost?: number | string;
}

interface AggregatedModelCost {
  model_name: string;
  provider: string;
  total_cost: number;
  input_cost: number;
  output_cost: number;
}

interface AIModelCostsChartProps {
  data: AIModelCostItem[] | null;
  loading: boolean;
}

const AIModelCostsChart: React.FC<AIModelCostsChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          AI Model Costs
        </h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          AI Model Costs
        </h3>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          No AI model cost data available
        </div>
      </div>
    );
  }

  // Aggregate costs by model
  const aggregatedData = data.reduce<Record<string, AggregatedModelCost>>((acc, item) => {
    const key = item.model_name;
    if (!acc[key]) {
      acc[key] = {
        model_name: item.model_name,
        provider: item.provider,
        total_cost: 0,
        input_cost: 0,
        output_cost: 0,
      };
    }
    acc[key].total_cost += Number.parseFloat(String(item.cost || 0));
    acc[key].input_cost += Number.parseFloat(String(item.input_cost || 0));
    acc[key].output_cost += Number.parseFloat(String(item.output_cost || 0));
    return acc;
  }, {});

  const aggregatedArray: AggregatedModelCost[] = Object.values(aggregatedData).sort(
    (a, b) => b.total_cost - a.total_cost
  );

  const maxCost = Math.max(...aggregatedArray.map(item => item.total_cost));

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
        AI Model Costs
      </h3>

      <div className="space-y-6">
        {aggregatedArray.slice(0, 5).map((item) => {
          const inputPercent = (item.input_cost / maxCost) * 100;
          const outputPercent = (item.output_cost / maxCost) * 100;

          return (
            <div key={item.model_name}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {item.model_name}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  ${item.total_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              {/* Stacked bar */}
              <div className="relative h-8 bg-gray-200 dark:bg-gray-700 rounded">
                {/* Input cost (darker shade) */}
                <div
                  className="absolute left-0 h-full bg-orange-500 rounded-l transition-all duration-300"
                  style={{ width: `${inputPercent}%` }}
                  title={`Input: $${item.input_cost.toFixed(2)}`}
                ></div>
                {/* Output cost (lighter shade) */}
                <div
                  className="absolute h-full bg-orange-300 transition-all duration-300"
                  style={{
                    left: `${inputPercent}%`,
                    width: `${outputPercent}%`
                  }}
                  title={`Output: $${item.output_cost.toFixed(2)}`}
                ></div>
              </div>

              {/* Cost breakdown labels */}
              <div className="flex items-center justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500 rounded-sm mr-1"></div>
                    <span>Input: ${item.input_cost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-300 rounded-sm mr-1"></div>
                    <span>Output: ${item.output_cost.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {aggregatedArray.length > 5 && (
        <div className="mt-6 text-center">
          <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
            View all {aggregatedArray.length} models
          </button>
        </div>
      )}
    </div>
  );
};

export default AIModelCostsChart;
